import React from 'react';
import {
  // Beast Part Icons
  Beef, Fish, Egg, Bone, Brain, Eye, 
  Heart, Circle, Triangle, Square, Diamond, Shield, Feather,
  
  // Bloom Type Icons  
  Apple, Cherry, Leaf, Flower, Nut, Bug, // Using Bug instead of Mushroom
  TreePine, Sprout, Banana, Grape,
  
  // Effect Icons
  Droplets, Flame, Sparkles, Zap, Star,
  
  // Fallback Icons
  HelpCircle, Package
} from 'lucide-react';/**
 * IngredientIcon Component
 * Renders appropriate icon and styling for any Beast Feast ingredient
 * Supports both beast parts and bloom types with color variations
 */
const IngredientIcon = ({ 
  ingredientName, 
  size = 24, 
  className = '',
  animate = false,
  glowing = false 
}) => {
  const { icon: IconComponent, colorClass, effects } = getIngredientVisual(ingredientName);
  
  const baseClasses = `transition-all duration-300 ${colorClass} ${className}`;
  const animationClasses = animate ? 'animate-pulse' : '';
  const glowClasses = glowing ? 'drop-shadow-lg filter brightness-110' : '';
  
  return (
    <div className={`relative inline-block ${effects.containerClass || ''}`}>
      <IconComponent 
        size={size}
        className={`${baseClasses} ${animationClasses} ${glowClasses}`}
      />
      {effects.overlay && (
        <div className={`absolute inset-0 ${effects.overlay}`} />
      )}
      {effects.glow && (
        <div className={`absolute inset-0 ${effects.glow} opacity-30 blur-sm -z-10`} />
      )}
    </div>
  );
};

/**
 * Parses ingredient name and returns appropriate visual representation
 */
const getIngredientVisual = (ingredientName) => {
  const lowerName = ingredientName.toLowerCase();
  
  // Extract traits (text in parentheses)
  const traitMatch = ingredientName.match(/\(([^)]+)\)/);
  const trait = traitMatch ? traitMatch[1].toLowerCase() : null;
  
  // Beast Parts Mapping
  const beastPartMappings = {
    // Meat & Flesh
    'meat': { icon: Beef, colorClass: 'text-red-500', category: 'meat' },
    'flesh': { icon: Beef, colorClass: 'text-red-400', category: 'meat' },
    'belly': { icon: Beef, colorClass: 'text-pink-400', category: 'meat' },
    'limb': { icon: Beef, colorClass: 'text-red-600', category: 'meat' },
    
    // Organs & Internals
    'organ': { icon: Heart, colorClass: 'text-purple-600', category: 'organ' },
    'brain': { icon: Brain, colorClass: 'text-pink-400', category: 'organ' },
    'tongue': { icon: Flame, colorClass: 'text-pink-500', category: 'organ' },
    'liver': { icon: Heart, colorClass: 'text-red-800', category: 'organ' },
    
    // Bones & Hard Parts
    'ribs': { icon: Bone, colorClass: 'text-gray-300', category: 'bone' },
    'marrow': { icon: Bone, colorClass: 'text-yellow-300', category: 'bone' },
    'horn': { icon: Triangle, colorClass: 'text-gray-600', category: 'bone' },
    'stones': { icon: Diamond, colorClass: 'text-gray-500', category: 'bone' },
    
    // External Parts
    'scales': { icon: Shield, colorClass: 'text-green-400', category: 'external' },
    'wings': { icon: Feather, colorClass: 'text-gray-400', category: 'external' },
    'eyes': { icon: Eye, colorClass: 'text-blue-400', category: 'external' },
    'feet': { icon: Circle, colorClass: 'text-brown-500', category: 'external' },
    
    // Fluids & Secretions
    'secretion': { icon: Droplets, colorClass: 'text-green-300', category: 'fluid' },
    'jelly': { icon: Droplets, colorClass: 'text-blue-300', category: 'fluid' },
    'fat': { icon: Circle, colorClass: 'text-yellow-400', category: 'fluid' },
    'powder': { icon: Sparkles, colorClass: 'text-gray-300', category: 'fluid' },
    
    // Special
    'eggs': { icon: Egg, colorClass: 'text-cream-200', category: 'special' },
  };
  
  // Bloom Types Mapping
  const bloomMappings = {
    // Fruits & Berries
    'fruit': { icon: Apple, colorClass: 'text-red-400', category: 'fruit' },
    'berries': { icon: Cherry, colorClass: 'text-purple-500', category: 'fruit' },
    'nuts': { icon: Nut, colorClass: 'text-amber-600', category: 'fruit' },
    'grain': { icon: Circle, colorClass: 'text-yellow-600', category: 'fruit' },
    
    // Plant Parts
    'leaves': { icon: Leaf, colorClass: 'text-green-500', category: 'plant' },
    'stems': { icon: TreePine, colorClass: 'text-green-600', category: 'plant' },
    'roots': { icon: TreePine, colorClass: 'text-amber-700', category: 'plant' },
    'bark': { icon: Shield, colorClass: 'text-amber-800', category: 'plant' },
    'bulbs': { icon: Circle, colorClass: 'text-purple-300', category: 'plant' },
    
    // Flowers & Decorative
    'flower': { icon: Flower, colorClass: 'text-pink-500', category: 'flower' },
    'pollen': { icon: Sparkles, colorClass: 'text-yellow-400', category: 'flower' },
    'nectar': { icon: Droplets, colorClass: 'text-gold-400', category: 'flower' },
    'petals': { icon: Flower, colorClass: 'text-pink-300', category: 'flower' },
    
    // Seeds & Small
    'seeds': { icon: Circle, colorClass: 'text-brown-400', category: 'seed' },
    'pods': { icon: Egg, colorClass: 'text-green-400', category: 'seed' },
    'herbs': { icon: Leaf, colorClass: 'text-green-400', category: 'seed' },
    
    // Fungi & Others
    'fungi': { icon: Bug, colorClass: 'text-amber-500', category: 'fungi' },
    'mushroom': { icon: Bug, colorClass: 'text-brown-500', category: 'fungi' },
    'moss': { icon: Sprout, colorClass: 'text-green-300', category: 'fungi' },
    'algae': { icon: Sprout, colorClass: 'text-teal-400', category: 'fungi' },
    
    // Fluids
    'sap': { icon: Droplets, colorClass: 'text-amber-300', category: 'fluid' },
    'rind': { icon: Shield, colorClass: 'text-orange-400', category: 'external' },
  };
  
  // Find matching ingredient type
  let result = null;
  
  // Check beast parts first
  for (const [key, value] of Object.entries(beastPartMappings)) {
    if (lowerName.includes(key)) {
      result = value;
      break;
    }
  }
  
  // If no beast part found, check blooms
  if (!result) {
    for (const [key, value] of Object.entries(bloomMappings)) {
      if (lowerName.includes(key)) {
        result = value;
        break;
      }
    }
  }
  
  // Default if nothing matches
  if (!result) {
    result = { icon: Circle, colorClass: 'text-gray-400', category: 'unknown' };
  }
  
  // Apply trait effects
  const effects = getTraitEffects(trait, result.category);
  
  return {
    icon: result.icon,
    colorClass: result.colorClass,
    effects
  };
};

/**
 * Returns visual effects based on ingredient traits
 */
const getTraitEffects = (trait, category) => {
  if (!trait) return {};
  
  const traitEffects = {
    'still wriggling': {
      containerClass: 'animate-bounce',
      glow: 'bg-red-400'
    },
    'brightly colored': {
      containerClass: 'animate-pulse',
      glow: 'bg-rainbow-400'
    },
    'translucent': {
      containerClass: 'opacity-70',
      overlay: 'bg-white bg-opacity-20'
    },
    'pungent smell': {
      glow: 'bg-green-400'
    },
    'smells good': {
      glow: 'bg-yellow-400'
    },
    'recoils from light': {
      containerClass: 'animate-pulse',
      glow: 'bg-purple-600'
    },
    'emitting gas': {
      glow: 'bg-green-300 animate-pulse'
    },
    'tender': {
      glow: 'bg-pink-200'
    },
    'brittle': {
      overlay: 'border border-gray-400 border-dashed'
    },
    'deadly': {
      glow: 'bg-red-600 animate-pulse'
    }
  };
  
  return traitEffects[trait] || {};
};

export default IngredientIcon;
